import type { FC } from "react";
import { isDevelopmentEnvironment } from "@/common/helpers/environment.helper";
import {
  MockMyHardwareService,
  SCENARIO_QUERY_PARAM,
} from "@/simulation/my-hardware/mock-my-hardware-service";
import { MyHardwareScenarioPicker } from "@/simulation/my-hardware/components/ScenarioPicker/scenario-picker";
import { HttpMyHardwareService } from "./http-my-hardware-service";
import type { MyHardwareService } from "./my-hardware.types";

export type MyHardwareServiceMode = "simulation" | "http";

export const resolveMyHardwareServiceMode = (
  hostname: string = window.location.hostname,
  search: string = window.location.search
): MyHardwareServiceMode =>
  isDevelopmentEnvironment(hostname) && new URLSearchParams(search).has(SCENARIO_QUERY_PARAM)
    ? "simulation"
    : "http";

const buildAdapter = (mode: MyHardwareServiceMode): MyHardwareService =>
  mode === "simulation" ? new MockMyHardwareService() : new HttpMyHardwareService();

const createModeSwitchingService = (): MyHardwareService => {
  const adapters = new Map<MyHardwareServiceMode, MyHardwareService>();
  const adapterFor = (mode: MyHardwareServiceMode): MyHardwareService => {
    const existing = adapters.get(mode);
    if (existing) return existing;
    const adapter = buildAdapter(mode);
    adapters.set(mode, adapter);
    return adapter;
  };
  return { getMyHardware: () => adapterFor(resolveMyHardwareServiceMode()).getMyHardware() };
};

export const createMyHardwareService = (mode?: MyHardwareServiceMode): MyHardwareService =>
  mode ? buildAdapter(mode) : createModeSwitchingService();

const NoDevTools: FC = () => null;

export const devToolsFor = (hostname: string): FC =>
  isDevelopmentEnvironment(hostname) ? MyHardwareScenarioPicker : NoDevTools;

export const MyHardwareDevTools: FC = devToolsFor(window.location.hostname);
